import uuid from 'uuid';
import {createAction} from 'redux-actions';
import {
  getCurrentDesignId,
  getLastTextIconPosition,
  getProductColorRemovalThreshold,
  getRecentUploadIcons,
  selectedIcon,
  selectedViewId,
  viewUploadedIcons
} from '~/selectors/CanvasSelectors';
import {
  getSelectedProductColor,
  getLaserEngravedDesignColor,
  getView,
  getViewConstraint,
  isAnchorLaserEngraved,
  getDecorationOverrideColor,
  getSelectedProductStyleType
} from '~/selectors/ProductSelectors';
import {getUploadIconData} from '~/selectors/UploadSelectors';
import {
  FILE_EXTENSIONS,
  ACCEPTED_FILE_EXTENSIONS,
  EXT_EXTRACTOR,
  MAXIMUM_FILE_SIZE,
  UNSUPPORTED_IMAGE_TYPES,
  UNSUPPORTED_VIDEO_TYPES
} from '~/constants/UploadConstants';
import UploadTypes from '~/constants/types/UploadTypes';
import RemoteServices from '~/constants/RemoteServices';
import {
  topLeftCanvasCoordinates,
  getUploadAutoSizeDimension
} from '~/data/accessors/canvas';
import {deserializeUpload} from '~/data/deserializers/uploadService';
import {difference, makeSpotColor} from '~/utils/colors';
import {setIconDecorationOverrideColors} from '~/utils/icon';
import DesignIconConstants from '~/constants/DesignIconConstants';
import {
  UPLOAD_DIMENSIONS_TOO_LARGE,
  UPLOAD_FILESIZE_TOO_LARGE,
  UPLOAD_FILE_UNSUPPORTED,
  COLOR_CHANGE_PANTONE_WARNING
} from '~/constants/NotificationConstants';
import {uploadColorRemoval} from '~/selectors/ConfigSelectors';
import {
  uploadRemoveColors,
  uploadSuccess as uploadSuccessAnalytics
} from '~/actions/AnalyticsActions';
import {error as errorNotification} from '~/actions/NotificationActions';
import {selectIcon} from '~/actions/SelectionActions';

import {getInkMatches} from '~/selectors/DesignSelectors';
import {metrics} from '~/vendor/metrics';
import {isUploadAutoSizingEnabled} from '~/selectors/ConfigSelectors';
import {STYLE_TYPE} from '~/constants/ProductConstants';

export const changeSpotColor =
  ({selected, ...payload}) =>
  (dispatch, getState) => {
    if (selected !== undefined) {
      const state = getState();
      const inkMatches = getInkMatches(state);

      const selectedColor =
        selected?.selectedUser ||
        selected?.user ||
        selected?.palette ||
        selected?.original;

      if (inkMatches?.[selectedColor?.hexString]) {
        return dispatch(
          errorNotification({
            key: COLOR_CHANGE_PANTONE_WARNING,
            attemptedColorChange: {
              colorChange: payload,
              hexString: selectedColor.hexString
            }
          })
        );
      }
    }

    dispatch({
      type: UploadTypes.CHANGE_SPOT_COLOR,
      payload
    });
  };

export const removeSpotColor = createAction(UploadTypes.REMOVE_SPOT_COLOR);

export const addSpotColor =
  ({iconId, color}) =>
  dispatch => {
    const spotColor = makeSpotColor(color);

    dispatch({
      type: UploadTypes.ADD_SPOT_COLOR,
      payload: {
        iconId,
        spotColor
      }
    });
  };

export const invalidFile = createAction(UploadTypes.INVALID_FILE);
export const processingUpload = createAction(UploadTypes.UPLOAD_IN_PROGRESS);
export const resetProgress = createAction(UploadTypes.RESET_PROGRESS);
export const resetUpload = createAction(UploadTypes.RESET_UPLOAD);
export const updateColorMode = createAction(UploadTypes.UPDATE_COLOR_MODE);
export const uploadSuccess = createAction(UploadTypes.UPLOAD_SUCCESS);

export const complete =
  ({uploadIconData}) =>
  (dispatch, getState) => {
    const state = getState();
    const designId = getCurrentDesignId(state);
    const recentUploadCopyId = uuid.v4();
    const view = getView(state, {
      viewIdentifier: uploadIconData.viewId
    });

    const isFirstUpload = viewUploadedIcons(state).size === 0;

    const constraint = getViewConstraint(state)(view);
    const productColor = getSelectedProductColor(state);
    const productStyleType = getSelectedProductStyleType(state);

    const uploadAutoSizing = isUploadAutoSizingEnabled(state);
    const isApparelWithUnsupportedView =
      productStyleType === STYLE_TYPE.APPAREL &&
      !['front', 'back'].includes(view.name);

    let iconData;
    if (uploadAutoSizing && !isApparelWithUnsupportedView) {
      const uploadAutoSizeDimension = getUploadAutoSizeDimension(
        constraint,
        uploadIconData,
        productStyleType,
        isFirstUpload
      );
      iconData = {
        ...uploadAutoSizeDimension,
        isAutoSize: true
      };
    } else {
      iconData = topLeftCanvasCoordinates(constraint);
    }

    const icon = {
      ...uploadIconData,
      ...iconData
    };

    const decorationOverrideColor = getDecorationOverrideColor(state);
    if (decorationOverrideColor || isAnchorLaserEngraved(state)) {
      setIconDecorationOverrideColors({
        iconType: DesignIconConstants.TYPES.upload,
        iconData: icon,
        decorationOverrideColor:
          decorationOverrideColor || getLaserEngravedDesignColor(state)
      });
    }

    if (uploadIconData.uploadAndSwap) {
      icon.selectedIcon = selectedIcon(state);
    }

    const actionType = uploadIconData.uploadAndSwap
      ? UploadTypes.UPLOAD_AND_SWAP
      : UploadTypes.UPLOAD_COMPLETE;

    const uploadColorRemovalFlag = uploadColorRemoval(state);
    if (icon.spotColors) {
      icon.hasColorBelowThreshold = icon.spotColors.some(spotColor => {
        return (
          getProductColorRemovalThreshold(state) >
          difference(spotColor.palette.hexString, productColor.hexString)
        );
      });
      if (icon.hasColorBelowThreshold) {
        dispatch(uploadRemoveColors());
      }
    }

    const layerPosition = getLastTextIconPosition(state);

    dispatch({
      type: actionType,
      payload: {
        designId,
        iconId: uploadIconData.iconId,
        icon,
        layerPosition,
        productColor,
        productColorRemovalThreshold: getProductColorRemovalThreshold(state),
        recentUploadCopyId,
        removeProductColors: uploadColorRemovalFlag
      }
    });
    dispatch(selectIcon({iconId: uploadIconData.iconId}));
    dispatch(resetProgress());

    return true;
  };

export function uploadFailed(error, uploadType = '') {
  return {
    type: UploadTypes.UPLOAD_FAILED,
    payload: {
      error,
      upload_type: uploadType
    }
  };
}

export const upload =
  ({file, uploadAndSwap, uploadType}) =>
  async (dispatch, getState) => {
    const state = getState();
    const viewId = selectedViewId(state);
    const iconId = uuid.v4();

    // add uploadtype here to payload
    dispatch(
      processingUpload({iconId, viewId, uploadAndSwap, upload_type: uploadType})
    );

    const formData = new FormData();
    formData.append('upload', file);
    const uploadUrl = RemoteServices.upload;
    const gamutUploadUrl =
      uploadUrl + '?' + new URLSearchParams({gamutMapping: 'true'});

    const requestData = {
      method: 'post',
      body: formData,
      credentials: 'omit'
    };

    const gamutMappingRequest = window.fetch(gamutUploadUrl, requestData);

    try {
      const gamutMappingResponse = await gamutMappingRequest;

      if (!gamutMappingResponse.ok) {
        if (gamutMappingResponse.status === 413) {
          dispatch(uploadFailed(UPLOAD_DIMENSIONS_TOO_LARGE));
        } else {
          dispatch(uploadFailed(UPLOAD_FILE_UNSUPPORTED));
        }
        return null;
      }

      const data = deserializeUpload(await gamutMappingResponse.json());

      dispatch(uploadSuccess(data));
      dispatch(
        uploadSuccessAnalytics({
          colorMode: data.colorMode,
          colorCount: data.spotColors?.size,
          gamutColorCount: data.spotColors?.size,
          extension: FILE_EXTENSIONS[file.type] || '',
          upload_type: uploadType
        })
      );

      const uploadIconData = getUploadIconData(getState());
      uploadIconData.iconId = iconId;
      uploadIconData.viewId = viewId;

      dispatch(complete({uploadIconData}));

      return data;
      // eslint-disable-next-line no-unused-vars
    } catch (err) {
      dispatch(uploadFailed(UPLOAD_FILE_UNSUPPORTED));
    }

    return null;
  };

export const removeProductColors =
  (iconId, removeProductColor) => (dispatch, getState) => {
    const state = getState();
    const productColor = getSelectedProductColor(state);
    if (iconId) {
      dispatch({
        type: UploadTypes.TOGGLE_PRODUCT_COLORS,
        payload: {
          iconId: iconId,
          color: productColor,
          remove: removeProductColor,
          threshold: getProductColorRemovalThreshold(state)
        }
      });
    }
  };

export const getUploadFile = uploadParams => (dispatch, getState) => {
  return window
    .fetch(`${RemoteServices.upload}/${uploadParams.url}`)
    .then(response => response.blob());
};

export const getOriginalUploadContentType = iconId => (dispatch, getState) => {
  if (!iconId) return null;

  const recentUploads = getRecentUploadIcons(getState());
  const recentUpload = recentUploads?.find(upload => upload?.id === iconId);
  const cleanRecentUploadKey = recentUpload?.url?.replace('/uploads/', '');

  return window
    .fetch(`${RemoteServices.upload}/v1/raw/${cleanRecentUploadKey}`)
    .then(response => response?.headers?.get('Content-Type'));
};

export const uploadFile =
  ({
    acceptedFile,
    rejectedFiles,
    onSuccess = () => {},
    onFailure = () => {},
    onUploadStarted = () => {},
    uploadAndSwap = false,
    uploadType = ''
  }) =>
  async dispatch => {
    if (acceptedFile && acceptedFile.size <= MAXIMUM_FILE_SIZE) {
      const ext = EXT_EXTRACTOR(acceptedFile.name?.toUpperCase());

      // windows OS does not specify 'type' for .psd files (and maybe some other files out there)
      // Therefore, checking file by its extension initially rather than by its type
      if (!Object.values(ACCEPTED_FILE_EXTENSIONS).includes(`.${ext}`)) {
        let formatType = 'other';

        if (UNSUPPORTED_IMAGE_TYPES.includes(acceptedFile.type)) {
          formatType = 'image';
        } else if (UNSUPPORTED_VIDEO_TYPES.includes(acceptedFile.type)) {
          formatType = 'video';
        }

        metrics.pageViewTag(
          `/ndx/upload/failure/${formatType}${
            ext ? '/' + ext.toLowerCase() : ''
          }`
        );
        dispatch(invalidFile(UPLOAD_FILE_UNSUPPORTED));
        onFailure();
        return;
      }
      onUploadStarted();
      dispatch(upload({file: acceptedFile, uploadAndSwap, uploadType}))
        .then(onSuccess)
        .catch(error => onFailure(error));
    } else if (rejectedFiles?.length) {
      dispatch(uploadFailed(UPLOAD_FILE_UNSUPPORTED, uploadType));
      dispatch(invalidFile(UPLOAD_FILE_UNSUPPORTED));
      onFailure();
    } else if (acceptedFile?.size > MAXIMUM_FILE_SIZE) {
      dispatch(uploadFailed(UPLOAD_FILESIZE_TOO_LARGE, uploadType));
      dispatch(invalidFile(UPLOAD_FILESIZE_TOO_LARGE));
      onFailure();
    }
  };
